import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { GrossPrice, VatIncludedLabel } from '@/components/billing/PricingBreakdown';
import type { PricingDisplayContract } from '@/lib/billing/pricingDisplay';
import { getConsentText } from '@/lib/billing/consentPaymentBinding';


interface RecurringBillingConsentProps {
  planName: string;
  billingCycle: 'monthly' | 'yearly';
  introPricing?: PricingDisplayContract | null;
  renewalPricing: PricingDisplayContract;
  platform?: 'forkhire' | 'forkseek';
}

export function RecurringBillingConsent({
  billingCycle,
  introPricing,
  renewalPricing,
}: RecurringBillingConsentProps) {
  const { t, i18n } = useTranslation();
  const freq = billingCycle === 'yearly' ? 'year' : 'month';
  const frequencyLabel = t(`hire.billingConsent.frequency.${freq}`);
  const hasIntro = Boolean(introPricing);

  return (
    <div className="space-y-3 rounded-xl border border-emerald-200/70 bg-emerald-50/45 p-4 dark:border-emerald-500/25 dark:bg-emerald-500/10">
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <div className="space-y-1.5">
          <p className="font-medium text-foreground text-sm">{t('hire.billingConsent.paymentDetails')}</p>
          {hasIntro && (
            <div>
              {t('hire.billingConsent.todayYouPayBefore')} <span className="inline-block align-middle"><GrossPrice pricing={introPricing!} size="small" /></span> {t('hire.billingConsent.todayYouPayAfter')}
            </div>
          )}
          <div>
            {t('hire.billingConsent.renewBefore')} <span className="inline-block align-middle"><GrossPrice pricing={renewalPricing} size="small" /></span> {t('hire.billingConsent.renewAfter', { frequency: frequencyLabel })}
          </div>
          <p className="text-[11px]"><VatIncludedLabel /> — {t('hire.billingConsent.vatIncluded')}</p>
          <p className="text-[11px]">{t('hire.billingConsent.cancelAnytime')}</p>
        </div>
      </div>

      <div className="rounded-lg bg-background/80 p-3 text-xs font-semibold leading-relaxed text-foreground">
        {getConsentText({
          planName: '',
          billingCycle,
          language: i18n.resolvedLanguage ?? i18n.language,
          introPricing,
          renewalPricing,
        })}
      </div>
    </div>
  );
}
